import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import useMusicKit from '../hooks/useMusicKit';
import { useAuth } from './auth';
import api from '../api';

var PlaybackContext = createContext();

var nextQueueId = 1;

// Suppress MusicKit's internal alert() calls for DRM/playback errors
var _originalAlert = window.alert;
window.alert = function (msg) {
  if (
    typeof msg === 'string' &&
    (msg.indexOf('MEDIA_KEY') !== -1 ||
      msg.indexOf('aborted') !== -1 ||
      msg.indexOf('Aborted') !== -1 ||
      msg.indexOf('operation') !== -1 ||
      msg.indexOf('Operation') !== -1)
  ) {
    console.warn('Suppressed MusicKit alert:', msg);
    return;
  }
  _originalAlert.call(window, msg);
};

function PlaybackProvider(props) {
  var musicKit = useMusicKit();

  var _currentTrack = useState(null);
  var currentTrack = _currentTrack[0];
  var _setCurrentTrack = _currentTrack[1];
  var currentTrackRef = useRef(null);
  function setCurrentTrack(track) {
    currentTrackRef.current = track;
    _setCurrentTrack(track);
  }

  var _playbackState = useState('idle');
  var playbackState = _playbackState[0];
  var setPlaybackState = _playbackState[1];

  var _progress = useState(0);
  var progress = _progress[0];
  var setProgress = _progress[1];

  var _currentTime = useState(0);
  var currentTime = _currentTime[0];
  var setCurrentTime = _currentTime[1];

  var _duration = useState(0);
  var duration = _duration[0];
  var setDuration = _duration[1];

  var _queue = useState([]);
  var queue = _queue[0];
  var setQueue = _queue[1];

  var _queueIndex = useState(-1);
  var queueIndex = _queueIndex[0];
  var setQueueIndex = _queueIndex[1];

  var _showQueue = useState(false);
  var showQueue = _showQueue[0];
  var setShowQueue = _showQueue[1];

  var audioRef = useRef(null);
  var isFullSongRef = useRef(false);
  var progressIntervalRef = useRef(null);
  var queueRef = useRef([]);
  var queueIndexRef = useRef(-1);
  var transitioningRef = useRef(false);
  var loadingQueueRef = useRef(false);
  var saveTimerRef = useRef(null);
  var audioHandlersRef = useRef({ ended: null, canplay: null });
  var transitionTimeoutRef = useRef(null);
  // Ref mirrors so event-listener closures always read current values
  var isAuthorizedRef = useRef(false);

  var auth = useAuth();
  var isLoggedIn = auth.isAuthenticated;

  // Keep refs in sync with state
  useEffect(
    function () {
      queueRef.current = queue;
    },
    [queue]
  );
  useEffect(
    function () {
      queueIndexRef.current = queueIndex;
    },
    [queueIndex]
  );
  useEffect(
    function () {
      isAuthorizedRef.current = musicKit.isAuthorized;
    },
    [musicKit.isAuthorized]
  );

  // Load queue from server on login
  useEffect(
    function () {
      if (!isLoggedIn) return;
      loadingQueueRef.current = true;
      api
        .get('/api/users/queue')
        .then(function (res) {
          var data = res.data;
          if (data && data.queue && data.queue.length > 0) {
            var tagged = data.queue.map(function (item) {
              return Object.assign({}, item, { id: nextQueueId++ });
            });
            setQueue(tagged);
            queueRef.current = tagged;
            var idx = data.queueIndex >= 0 && data.queueIndex < tagged.length ? data.queueIndex : 0;
            setQueueIndex(idx);
            queueIndexRef.current = idx;
            // Show track in MiniPlayer (paused, not playing)
            var item = tagged[idx];
            setCurrentTrack({
              postId: item.postId,
              title: item.title,
              artist: item.artist,
              artworkURL: item.artworkURL,
              isFullSong: !!(isAuthorizedRef.current && item.catalogID),
            });
          }
        })
        .catch(function () {
          /* queue stays empty */
        })
        .then(function () {
          loadingQueueRef.current = false;
        });
    },
    [isLoggedIn]
  );

  // Debounced save queue to server on change
  useEffect(
    function () {
      if (!isLoggedIn) return;
      if (loadingQueueRef.current) return;
      if (queue.length === 0 && queueIndex === -1) return;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(function () {
        var stripped = queue.map(function (item) {
          return {
            postId: item.postId || null,
            title: item.title || null,
            artist: item.artist || null,
            artworkURL: item.artworkURL || null,
            catalogID: item.catalogID || null,
            previewURL: item.previewURL || null,
            isrc: item.isrc || null,
            upc: item.upc || null,
          };
        });
        api.put('/api/users/queue', { queue: stripped, queueIndex: queueIndex }).catch(function () {
          /* ignore save errors */
        });
      }, 2000);

      return function () {
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
      };
    },
    [queue, queueIndex, isLoggedIn]
  );

  // ── Helpers ──

  function _clearTransitionTimeout() {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }

  function _endTransition() {
    transitioningRef.current = false;
    _clearTransitionTimeout();
  }

  // Update browser Media Session metadata so the OS knows we're playing audio.
  // This prevents background tab throttling. Only next/previous action handlers
  // are registered -- play/pause are left to MusicKit to avoid conflicts.
  function _updateMediaSession(track, state) {
    if (!navigator.mediaSession) return;
    if (!track) {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
      return;
    }
    var artworkURL = (track.artworkURL || '').replace('{w}x{h}', '256x256');
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title || '',
      artist: track.artist || '',
      album: '',
      artwork: artworkURL ? [{ src: artworkURL, sizes: '256x256', type: 'image/jpeg' }] : [],
    });
    navigator.mediaSession.playbackState = state === 'playing' ? 'playing' : 'paused';
  }

  // Register next/previous handlers for lock screen and notification controls
  useEffect(function () {
    if (!navigator.mediaSession) return;
    try {
      navigator.mediaSession.setActionHandler('nexttrack', function () {
        _playNextOrStop();
      });
      navigator.mediaSession.setActionHandler('previoustrack', function () {
        if (queueIndexRef.current > 0) {
          var prevIdx = queueIndexRef.current - 1;
          queueIndexRef.current = prevIdx;
          _startPlayingItem(queueRef.current[prevIdx]);
        }
      });
    } catch (e) {
      // Some handlers may not be supported
    }
  }, []);

  function _resetProgress() {
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  }

  function startProgressPolling() {
    stopProgressPolling();
    progressIntervalRef.current = setInterval(function () {
      if (isFullSongRef.current) {
        var instance = window.MusicKit?.getInstance();
        if (instance) {
          var ct = instance.currentPlaybackTime || 0;
          var dur = instance.currentPlaybackDuration || 0;
          setCurrentTime(ct);
          setDuration(dur);
          setProgress(dur > 0 ? ct / dur : 0);
        }
      } else if (audioRef.current) {
        var ct2 = audioRef.current.currentTime || 0;
        var dur2 = audioRef.current.duration || 0;
        setCurrentTime(ct2);
        setDuration(dur2);
        setProgress(dur2 > 0 ? ct2 / dur2 : 0);
      }
    }, 500);
  }

  function stopProgressPolling() {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }

  // ── Stop current audio without clearing queue ──

  function _stopCurrentAudio() {
    stopProgressPolling();

    if (isFullSongRef.current) {
      var instance = window.MusicKit?.getInstance();
      if (instance) {
        instance.stop();
      }
    }

    if (audioRef.current) {
      audioRef.current.pause();
      // Remove event listeners before dropping reference
      if (audioHandlersRef.current.ended) {
        audioRef.current.removeEventListener('ended', audioHandlersRef.current.ended);
      }
      if (audioHandlersRef.current.canplay) {
        audioRef.current.removeEventListener('canplay', audioHandlersRef.current.canplay);
      }
      audioRef.current = null;
    }
    audioHandlersRef.current = { ended: null, canplay: null };

    isFullSongRef.current = false;
    _resetProgress();
  }

  // ── Play a single queue item ──

  // Whether `item` is still the queue's current entry. A lookup that comes
  // back after the reader has moved on (tapped another song, skipped) must
  // not start its song over the one now playing, or stop it.
  function _isCurrent(item) {
    var q = queueRef.current;
    var cur = q && q[queueIndexRef.current];
    return !!cur && cur.id === item.id;
  }

  function _startPlayingItem(item) {
    transitioningRef.current = true;
    _clearTransitionTimeout();
    // Safety net: clear transitioning after 10s if nothing else does
    transitionTimeoutRef.current = setTimeout(function () {
      transitioningRef.current = false;
    }, 10000);

    _stopCurrentAudio();

    setCurrentTrack({
      postId: item.postId,
      title: item.title,
      artist: item.artist,
      artworkURL: item.artworkURL,
      isFullSong: false,
    });
    setPlaybackState('loading');

    // Use ref so MusicKit event-listener closures see current auth state
    var mkInstance = window.MusicKit?.getInstance();
    var useFullSong = isAuthorizedRef.current && item.catalogID && mkInstance;

    if (useFullSong) {
      isFullSongRef.current = true;
      setCurrentTrack({
        postId: item.postId,
        title: item.title,
        artist: item.artist,
        artworkURL: item.artworkURL,
        isFullSong: true,
      });

      mkInstance
        .setQueue({ songs: [item.catalogID], startPlaying: true })
        .then(function () {
          // transitioningRef cleared by playbackStateDidChange when state === 2 (playing)
          startProgressPolling();
          if (isLoggedIn && item.postId) {
            api
              .post('/api/play-events', { postId: item.postId, playType: 'full_play' })
              .catch(function () {});
          }
        })
        .catch(function () {
          _endTransition();
          setPlaybackState('idle');
          isFullSongRef.current = false;
        });
    } else if (item.previewURL) {
      isFullSongRef.current = false;
      var audio = new Audio(item.previewURL);
      audioRef.current = audio;

      // Store handler refs so _stopCurrentAudio can remove them
      var onEnded = function () {
        _playNextOrStop();
      };
      var onCanplay = function () {
        setDuration(audio.duration || 0);
      };
      audioHandlersRef.current = { ended: onEnded, canplay: onCanplay };
      audio.addEventListener('ended', onEnded);
      audio.addEventListener('canplay', onCanplay);

      audio
        .play()
        .then(function () {
          _endTransition();
          setPlaybackState('playing');
          startProgressPolling();
          if (isLoggedIn && item.postId) {
            api
              .post('/api/play-events', { postId: item.postId, playType: 'preview_play' })
              .catch(function () {});
          }
        })
        .catch(function () {
          _endTransition();
          setPlaybackState('idle');
          audioRef.current = null;
          audioHandlersRef.current = { ended: null, canplay: null };
        });
    } else if (item.upc && !item.isrc) {
      // Album with no tracks yet -- fetch full track list and expand into queue
      api
        .get('/api/music/album-tracks', { params: { upc: item.upc } })
        .then(function (res) {
          if (!_isCurrent(item)) return;
          var tracks = (res.data && res.data.tracks) || [];
          if (tracks.length === 0) {
            _endTransition();
            _playNextOrStop();
            return;
          }
          var expandedTracks = tracks.map(function (t) {
            return {
              id: nextQueueId++,
              postId: item.postId,
              // Whatever queued the album still knows its tracks as its own.
              sourceKey: item.sourceKey,
              title: t.title || '',
              artist: item.artist,
              artworkURL: item.artworkURL,
              catalogID: t.catalogID || null,
              previewURL: t.previewURL || null,
              isrc: null,
              upc: null,
            };
          });
          // Replace the album item with its tracks in the queue
          var currentIdx = queueIndexRef.current;
          setQueue(function (prev) {
            var idx = prev.findIndex(function (q) {
              return q.id === item.id;
            });
            if (idx === -1) return prev;
            var updated = prev
              .slice(0, idx)
              .concat(expandedTracks)
              .concat(prev.slice(idx + 1));
            queueRef.current = updated;
            return updated;
          });
          // Start playing the first track of the expanded album
          _startPlayingItem(expandedTracks[0]);
        })
        .catch(function () {
          if (!_isCurrent(item)) return;
          _endTransition();
          _playNextOrStop();
        });
    } else if (item.isrc) {
      // Song with no preview data yet -- fetch it lazily then retry
      api
        .get('/api/music/preview', { params: { isrc: item.isrc } })
        .then(function (res) {
          if (!_isCurrent(item)) return;
          var data = res.data || {};
          item.previewURL = data.previewURL || null;
          item.catalogID = data.catalogID || null;
          // Update the queue entry
          setQueue(function (prev) {
            return prev.map(function (q) {
              return q.id === item.id
                ? Object.assign({}, q, { previewURL: item.previewURL, catalogID: item.catalogID })
                : q;
            });
          });
          // Playable now only with a preview, or a catalog id the full-song
          // player can use. A catalog id alone, with Apple Music not
          // connected, would come straight back here: an endless loop of
          // lookups.
          var canPlayFull =
            isAuthorizedRef.current && item.catalogID && window.MusicKit?.getInstance();
          if (item.previewURL || canPlayFull) {
            _startPlayingItem(item);
          } else {
            _endTransition();
            _playNextOrStop();
          }
        })
        .catch(function () {
          if (!_isCurrent(item)) return;
          _endTransition();
          _playNextOrStop();
        });
    } else {
      // No way to play this item, try next
      _endTransition();
      _playNextOrStop();
    }
  }

  // ── Advance to next track or stop ──

  function _playNextOrStop() {
    var q = queueRef.current;
    var idx = queueIndexRef.current;
    if (idx + 1 < q.length) {
      var nextIdx = idx + 1;
      setQueueIndex(nextIdx);
      queueIndexRef.current = nextIdx;
      _startPlayingItem(q[nextIdx]);
    } else {
      // End of queue
      _endTransition();
      _stopCurrentAudio();
      setPlaybackState('idle');
      _updateMediaSession(null, 'idle');
    }
  }

  // ── MusicKit playback state listener ──

  useEffect(
    function () {
      if (musicKit.isLoading) return;

      var instance = window.MusicKit?.getInstance();
      if (!instance) return;

      function handlePlaybackStateChange(event) {
        if (!isFullSongRef.current) return;

        var state = event.state;
        // MusicKit PlaybackStates: none=0, loading=1, playing=2, paused=3, stopped=4, ended=5, waiting=6
        if (state === 4 || state === 0) {
          // Stopped programmatically -- skip cleanup if transitioning between tracks
          if (transitioningRef.current) return;
          setPlaybackState('idle');
          _resetProgress();
          stopProgressPolling();
          isFullSongRef.current = false;
        } else if (state === 2) {
          _endTransition();
          setPlaybackState('playing');
          _updateMediaSession(currentTrackRef.current, 'playing');
        } else if (state === 3) {
          setPlaybackState('paused');
          _updateMediaSession(currentTrackRef.current, 'paused');
        } else if (state === 5) {
          // Track ended naturally -- advance queue
          _playNextOrStop();
        } else if (state === 1 || state === 6) {
          setPlaybackState('loading');
        }
      }

      function handleMediaPlaybackError(event) {
        // Suppress MusicKit DRM/MEDIA_KEY errors -- nothing we can do about them
        console.warn('MusicKit playback error:', event);
        _endTransition();
        setPlaybackState('idle');
        _resetProgress();
        stopProgressPolling();
        isFullSongRef.current = false;
      }

      instance.addEventListener('playbackStateDidChange', handlePlaybackStateChange);
      instance.addEventListener('mediaPlaybackError', handleMediaPlaybackError);

      return function () {
        instance.removeEventListener('playbackStateDidChange', handlePlaybackStateChange);
        instance.removeEventListener('mediaPlaybackError', handleMediaPlaybackError);
      };
    },
    [musicKit.isLoading]
  );

  // ── Upgrade preview to full song when Apple Music is connected mid-session ──

  useEffect(
    function () {
      if (!musicKit.isAuthorized) return;
      if (isFullSongRef.current) return; // already playing full song

      var q = queueRef.current;
      var idx = queueIndexRef.current;
      if (idx < 0 || idx >= q.length) return;
      if (!q[idx].catalogID) return;

      // Only upgrade if actively playing/paused on a preview
      if (audioRef.current) {
        _startPlayingItem(q[idx]);
      }
    },
    [musicKit.isAuthorized]
  );

  // ── Cleanup on unmount ──

  useEffect(function () {
    return function () {
      stopProgressPolling();
      _clearTransitionTimeout();
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioHandlersRef.current.ended) {
          audioRef.current.removeEventListener('ended', audioHandlersRef.current.ended);
        }
        if (audioHandlersRef.current.canplay) {
          audioRef.current.removeEventListener('canplay', audioHandlersRef.current.canplay);
        }
        audioRef.current = null;
      }
    };
  }, []);

  // ── Public API ──

  // Replace queue and start playing
  var playNow = useCallback(
    function (items) {
      transitioningRef.current = true;
      _stopCurrentAudio();
      var tagged = items.map(function (item) {
        return Object.assign({}, item, { id: item.id || nextQueueId++ });
      });
      setQueue(tagged);
      queueRef.current = tagged;
      setQueueIndex(0);
      queueIndexRef.current = 0;
      if (tagged.length > 0) {
        _startPlayingItem(tagged[0]);
      }
    },
    [musicKit.isAuthorized]
  );

  // Append items to end of queue
  var addToQueue = useCallback(function (items) {
    var tagged = items.map(function (item) {
      return Object.assign({}, item, { id: item.id || nextQueueId++ });
    });
    setQueue(function (prev) {
      var updated = prev.concat(tagged);
      queueRef.current = updated;
      return updated;
    });
  }, []);

  var playNext = useCallback(
    function () {
      if (queueIndexRef.current + 1 < queueRef.current.length) {
        var nextIdx = queueIndexRef.current + 1;
        setQueueIndex(nextIdx);
        queueIndexRef.current = nextIdx;
        _startPlayingItem(queueRef.current[nextIdx]);
      }
    },
    [musicKit.isAuthorized]
  );

  var playPrevious = useCallback(
    function () {
      if (queueIndexRef.current > 0) {
        var prevIdx = queueIndexRef.current - 1;
        setQueueIndex(prevIdx);
        queueIndexRef.current = prevIdx;
        _startPlayingItem(queueRef.current[prevIdx]);
      }
    },
    [musicKit.isAuthorized]
  );

  var jumpToQueueItem = useCallback(
    function (idx) {
      if (idx >= 0 && idx < queueRef.current.length) {
        setQueueIndex(idx);
        queueIndexRef.current = idx;
        _startPlayingItem(queueRef.current[idx]);
      }
    },
    [musicKit.isAuthorized]
  );

  var removeFromQueue = useCallback(
    function (id) {
      setQueue(function (prev) {
        var removeIdx = -1;
        for (var i = 0; i < prev.length; i++) {
          if (prev[i].id === id) {
            removeIdx = i;
            break;
          }
        }
        if (removeIdx === -1) return prev;

        var updated = prev.filter(function (_, i) {
          return i !== removeIdx;
        });
        queueRef.current = updated;

        // Adjust index
        var currentIdx = queueIndexRef.current;
        if (removeIdx < currentIdx) {
          setQueueIndex(currentIdx - 1);
          queueIndexRef.current = currentIdx - 1;
        } else if (removeIdx === currentIdx) {
          // Removed the currently playing track
          if (updated.length === 0) {
            _stopCurrentAudio();
            _endTransition();
            setPlaybackState('idle');
            setCurrentTrack(null);
            setQueueIndex(-1);
            queueIndexRef.current = -1;
          } else if (currentIdx < updated.length) {
            _startPlayingItem(updated[currentIdx]);
          } else {
            var newIdx = updated.length - 1;
            setQueueIndex(newIdx);
            queueIndexRef.current = newIdx;
            _startPlayingItem(updated[newIdx]);
          }
        }

        return updated;
      });
    },
    [musicKit.isAuthorized]
  );

  var clearQueue = useCallback(
    function () {
      _stopCurrentAudio();
      _endTransition();
      setQueue([]);
      queueRef.current = [];
      setQueueIndex(-1);
      queueIndexRef.current = -1;
      setPlaybackState('idle');
      setCurrentTrack(null);
      setShowQueue(false);
      // Persist empty queue to server
      if (isLoggedIn) {
        api.put('/api/users/queue', { queue: [], queueIndex: -1 }).catch(function () {
          /* ignore save errors */
        });
      }
    },
    [isLoggedIn]
  );

  var stop = useCallback(
    function () {
      clearQueue();
    },
    [clearQueue]
  );

  // Legacy wrappers -- wrap single item in queue
  var playPreview = useCallback(
    function (postId, media, previewURL) {
      var title = media.type === 'album' ? media.album_title : media.song_title;
      var artist = media.artist;
      var artworkURL = media.artwork_url;

      playNow([
        {
          postId: postId,
          title: title,
          artist: artist,
          artworkURL: artworkURL,
          catalogID: null,
          previewURL: previewURL,
        },
      ]);
    },
    [playNow]
  );

  var playFullSong = useCallback(
    function (postId, media, catalogID) {
      var title = media.type === 'album' ? media.album_title : media.song_title;
      var artist = media.artist;
      var artworkURL = media.artwork_url;

      playNow([
        {
          postId: postId,
          title: title,
          artist: artist,
          artworkURL: artworkURL,
          catalogID: catalogID,
          previewURL: null,
        },
      ]);
    },
    [playNow]
  );

  var togglePlayPause = useCallback(
    function () {
      if (playbackState === 'playing') {
        // ── Pause ──
        if (isFullSongRef.current) {
          var instance = window.MusicKit?.getInstance();
          if (instance) instance.pause();
        } else if (audioRef.current) {
          audioRef.current.pause();
        }
        setPlaybackState('paused');
        stopProgressPolling();
      } else if (playbackState === 'paused') {
        // ── Resume ──
        if (isFullSongRef.current) {
          var instance2 = window.MusicKit?.getInstance();
          if (instance2) instance2.play();
          // playbackStateDidChange listener handles state + polling
        } else if (audioRef.current) {
          audioRef.current
            .play()
            .then(function () {
              setPlaybackState('playing');
              startProgressPolling();
            })
            .catch(function () {
              // Resume failed -- stay paused
            });
        }
      } else if (playbackState === 'idle') {
        // ── Start playing current queue item (e.g., queue loaded from server) ──
        var q = queueRef.current;
        var idx = queueIndexRef.current;
        if (idx >= 0 && idx < q.length) {
          _startPlayingItem(q[idx]);
        }
      }
      // "loading" -- already in progress, do nothing
    },
    [playbackState, musicKit.isAuthorized]
  );

  var seekTo = useCallback(function (fraction) {
    if (isFullSongRef.current) {
      var instance = window.MusicKit?.getInstance();
      if (instance && instance.currentPlaybackDuration) {
        instance.seekToTime(fraction * instance.currentPlaybackDuration);
      }
    } else if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = fraction * audioRef.current.duration;
    }
  }, []);

  var hasNext = queueIndex + 1 < queue.length;
  var hasPrevious = queueIndex > 0;

  var value = {
    // MusicKit auth state
    isAppleMusicConnected: musicKit.isAuthorized,
    musicKitLoading: musicKit.isLoading,
    authorize: musicKit.authorize,
    unauthorize: musicKit.unauthorize,
    // Playback state
    currentTrack: currentTrack,
    playbackState: playbackState,
    progress: progress,
    currentTime: currentTime,
    duration: duration,
    // Queue state
    queue: queue,
    queueIndex: queueIndex,
    hasNext: hasNext,
    hasPrevious: hasPrevious,
    // Actions
    playPreview: playPreview,
    playFullSong: playFullSong,
    playNow: playNow,
    addToQueue: addToQueue,
    playNext: playNext,
    playPrevious: playPrevious,
    jumpToQueueItem: jumpToQueueItem,
    removeFromQueue: removeFromQueue,
    clearQueue: clearQueue,
    togglePlayPause: togglePlayPause,
    stop: stop,
    seekTo: seekTo,
    showQueue: showQueue,
    setShowQueue: setShowQueue,
  };

  return React.createElement(PlaybackContext.Provider, { value: value }, props.children);
}

function usePlayback() {
  var context = useContext(PlaybackContext);
  if (context === undefined) {
    throw new Error('usePlayback must be used within a PlaybackProvider');
  }
  return context;
}

export { PlaybackContext, PlaybackProvider, usePlayback };
